import React, {useCallback, useEffect, useState} from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Avatar from "@mui/material/Avatar";
import Chip from '@mui/material/Chip';
import {Typography} from "@material-ui/core";
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import MenuItem from "@mui/material/MenuItem";
import {TextField} from "@mui/material";
import {findById, getJobById} from "../../../utilities/cvAPI";
import keycloak from "../../../Keycloak";
import Enums from "../../General/Enums";
import Modal from "@mui/material/Modal";
import {findCandidateById, getUsersByGroup, postNotes} from "../../../utilities/HrAPI";
import {toast} from "react-toastify";
import DecisionModal from "./Modals/DecisionModal";
import CircularProgress from "@mui/material/CircularProgress";
import DecisionModalSettings from "./Modals/DecisionModalSettings";
import {DECISION_TYPES} from "../../CV/DecisionTypes";
import {styled} from "@mui/material/styles";

const LowercaseButton = styled(Button)({
    textTransform: 'lowercase',
});

const decisionOptions = Object.entries(DECISION_TYPES)
    .map(([key, { label }]) => ({
        value: key,
        label: label
}));

const referenceChecks = [
    {
        value: "id",
        label: "ID document certified"
    },
    {
        value: "drivers license",
        label: "Drivers License"
    },
    {
        value: "employee references",
        label: "3 Employee references"
    },
    {
        value: "University transcripts",
        label: "University transcripts"
    },
]

const declineReasons = [
    {
        value: 0,
        label: "Candidate did not meet basic requirements"
    },
    {
        value: 1,
        label: "Candidate is ineligible to work in location"
    },
    {
        value: 2,
        label: "More qualified candidate selected"
    },
    {
        value: 3,
        label: "Candidate withdrew",
    },
    {
        value: 4,
        label: "Candidate not considered",
    },
    {
        value: 5,
        label: "Other"
    }
]

const defaultStatus = {
    name: "AWAITING_SCORE",
    description: "Awaiting scoring",
    details: ""
};

const defaultCandidate = {
    candidateID: 0,
    fullName: "",
    appliedOn: "",
    jobID: 0,
    status: defaultStatus,
    cvScore: 0,
    cvID: 0
};

export default function CandidateHeader({candidateId, candidateCvId, score, onStatusUpdate}) {
    const [jobId, setJobId] = useState(0);
    const [cv, setCV] = useState(Enums.cv);
    const [candidate, setCandidate] = useState(defaultCandidate)
    const [jobTitle, setJobTitle] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showDecisionModal, setShowDecisionModal] = useState(false);
    const [modalHeading, setModalHeading] = useState("");
    const [modalSettings, setModalSettings] = useState(null)
    const [notes, setNotes] = useState({
        id: 0,
        note: "Test...",
        candidateId: candidateId,
        date: new Date(),
    })
    const [isLoading, setIsLoading] = useState(true);
    const [hiringManagers, setHiringManagers] = useState([]);

    const style = {
        "& label.Mui-focused": {
            color: "#FF6600",
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: "#FF6600",
            },
        },
    };

    const showSaveSuccess = useCallback((message) => {
        toast.success(message, {
            position: toast.POSITION.TOP_LEFT,
            style: {
                backgroundColor: "#49c96d",
                width: "650px",
                color: "#FFFFFF",
                left: "500px",
                theme: "colored",
            }
        });
    }, []);

    const saveNote = useCallback(() => {
        setNotes((prevNotes) => ({
            ...prevNotes,
            date: new Date(),
        }));

        postNotes(notes, keycloak.token)
            .then(() => {
                showSaveSuccess("New note added");
            })
            .catch((e) => {
                console.error("Failed to save note:", e);
                toast.error("Failed to save note");
            });
        setShowModal(false);
    }, [notes, showSaveSuccess]);

    const closeModal = useCallback(() => {
        setShowModal(false);
    }, []);

    const openModal = useCallback(() => {
        setShowModal(true);
    }, []);

    const handleChange = useCallback((e) => {
        const { value } = e.target;
        setNotes((prevNotes) => ({
            ...prevNotes,
            note: value,
        }));
    }, []);

    const handleSelect = (e) => {
        let { value } = e.target;
        setModalHeading(e.target.value);

        const newSettings = getModalSettingsByDecision(value)
        setModalSettings(newSettings);

        if (newSettings) {
            setShowDecisionModal(true);
        }
    }

    const getModalSettingsByDecision = (decision) => {
        switch (decision) {
            case 'DECLINE':
                return new DecisionModalSettings(declineReasons, 1, "Select reason");
            case 'NOTIFY_MANAGER':
                return new DecisionModalSettings(hiringManagers, 1, "Select Manager");
            case 'REFERENCE_CHECKS':
                return new DecisionModalSettings(referenceChecks, 2, "Please select the documents you want from the candidate");
            case 'INITIAL_INTERVIEW':
            case 'FOLLOWUP_INTERVIEW':
            case 'PSYCHOMETRICS':
            case 'OFFER_LETTER':
                return new DecisionModalSettings(null, 3, "Select due date");
            default:
                return null;
        }
    };

    const fetchHiringManagers = useCallback(async () => {
        try {
            const users = await getUsersByGroup(keycloak.token);
            const managers = users.map(user => ({
                value: user.email,
                label: `${user.firstName} ${user.surname}`
            }));
            setHiringManagers(managers);
        } catch (e) {
            console.error('Failed to fetch hiring managers:', e);
        }
    }, []);

    const callCandidateByCvId = useCallback(async () => {
        try {
            const cvData = await findById(candidateCvId, keycloak.token);
            if (!cvData) {
                console.error('No CV data found.');
                return;
            }
            setCV(cvData);

            const candidateData = await findCandidateById(candidateId, keycloak.token);

            setJobId(cvData.jobHash);

            const job = await getJobById(cvData.jobHash);
            if (job) {
                setJobTitle(job.jobTitle);
            } else {
                setJobTitle('Job title not found');
            }

            if (candidateData) {
                setCandidate(candidateData);
            } else {
                console.error('No candidate data found.');
            }
        } catch (e) {
            console.error('Failed to fetch data:', e);
        }
    }, [candidateId, candidateCvId]);

    useEffect(() => {
        if ((keycloak.hasResourceRole("hr_admin") || keycloak.hasResourceRole("client_recruiter")) && candidateId) {
            callCandidateByCvId().then(() => setIsLoading(false));
            fetchHiringManagers();
        }
    }, [candidateId, callCandidateByCvId, fetchHiringManagers]);

    useEffect(() => {
        const statusName = candidate?.status?.name;
        if (statusName) {
            setModalHeading(statusName);
            onStatusUpdate(statusName);
        }
    }, [candidate, onStatusUpdate]);

    useEffect(() => {
        if (score !== null) {
            setCandidate(prevCandidate => ({
                ...prevCandidate,
                cvScore: score,
                status: {
                    name: "NO_DECISION",
                    description: "Pending Decision",
                    details: ""
                }
            }));
        }
    }, [score]);

    const isScored = candidate.status.name === 'AWAITING_SCORE';

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <React.Fragment>
            <Modal
                data-testid="notesModal"
                open={showModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={Enums.modalStyle}>
                    <Typography id="modal-modal-title" variant="h4" component="h2">
                        Add Note
                    </Typography>
                    <Grid container direction="row" alignItems="flex-end" padding={2} spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                data-testid="notes-textField"
                                multiline
                                rows={8}
                                onChange={handleChange}
                                sx={style}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <Button
                                variant="outlined"
                                data-testid="cancel"
                                onClick={closeModal}
                                sx={{
                                    float: 'right',
                                    width: '89px',
                                    height: '40px',
                                    fontFamily: 'Poppins',
                                    color: '#FF6600',
                                    borderColor: '#FF6600',
                                }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item xs={2}>
                            <Button
                                onClick={saveNote}
                                data-testid="save"
                                sx={{
                                    float: 'right',
                                    backgroundColor: '#FF6600',
                                    width: '89px',
                                    height: '40px',
                                    fontFamily: 'Poppins',
                                    color: '#FFFFFF',
                                    marginLeft: 'auto',
                                }}
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>

            <Modal
                data-testid="decisionModal"
                open={showDecisionModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <DecisionModal
                    heading={modalHeading}
                    modalSettings={modalSettings}
                    setCandidate={setCandidate}
                    candidate={candidate}
                    cvId={cv.id}
                    setShowDecisionModal={setShowDecisionModal}
                />
            </Modal>

            <Box data-testid="candidate-header" sx={{ maxHeight: '15%' }}>
                <Grid container padding={2} spacing={2} justifyContent="space-around">
                    <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Avatar sx={{ width: '169px', height: '169px', fontSize: '500%', borderRadius: '50%' }}>
                            {cv.personalDetails.name.charAt(0).toUpperCase() + cv.personalDetails.surname.charAt(0).toUpperCase()}
                        </Avatar>
                    </Grid>

                    <Grid item xs={5}>
                        <Grid container direction="column" spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6">
                                    {`${cv.personalDetails.name} ${cv.personalDetails.surname}`} <Chip label={candidate?.status.description || 'Candidate CV Error'} />
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="caption" data-testid="job-title">
                                    <LowercaseButton
                                        data-testid="job-link"
                                        href={`/JobView?jobID=${jobId}`}
                                        sx={{
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            background: 'none',
                                            fontSize: '0.800rem',
                                        }}
                                    >
                                        <AccountCircleIcon /> {jobTitle}
                                    </LowercaseButton>
                                </Typography>
                                <Typography variant="caption">
                                    <PhoneIcon /> {cv.personalDetails.phoneNum}
                                </Typography>
                                <Typography variant="caption">
                                    <EmailIcon /> {cv.personalDetails.email}
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={9}>
                                        <Card sx={{ width: '100%', minHeight: '50px' }}>
                                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="h4" data-testid="status-card">
                                                    {candidate?.status.description || 'Candidate CV Error'}
                                                </Typography>
                                                <Typography className="text-5">Current Status</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <Card sx={{ minWidth: '100px', minHeight: '50px' }}>
                                            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="h4" data-testid="score-card">
                                                    {score !== null ? score : cv.scoreSummary?.score}%
                                                </Typography>
                                                <Typography className="text-5">Score</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={5}>
                        <Button
                            onClick={openModal}
                            variant="outlined"
                            data-testid="add-notes"
                            sx={{
                                width: '185px',
                                height: '40px',
                                fontFamily: 'Poppins',
                                top: '81%',
                                color: '#ff6600',
                                borderColor: '#FF6600',
                            }}
                        >
                            <AddIcon />Add Note
                        </Button>
                        <TextField
                            label="Pending decision"
                            select
                            value={modalHeading}
                            data-testid="select-decision"
                            onChange={handleSelect}
                            sx={style}
                            fullWidth
                            disabled={isScored}
                            style={{ marginLeft: '2%', width: '45%', top: '77.5%' }}
                        >
                            <MenuItem value={modalHeading} data-testid={modalHeading} style={{ display: 'none' }}>
                                {decisionOptions.find(option => option.value === modalHeading)?.label || modalHeading}
                            </MenuItem>

                            {decisionOptions
                                .filter(option => option.value !== 'AWAITING_SCORE')
                                .map(option => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        data-testid={`${option.value}`}
                                        disabled={option.value === 'NO_DECISION'}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Box>
        </React.Fragment>
    );
}